import { API_RETRY_ATTEMPTS, API_RETRY_DELAY_MS } from "./constants";

/**
 * Retries a function with exponential backoff
 * @param fn Function to retry
 * @param maxRetries Maximum number of retry attempts
 * @param delayMs Initial delay in milliseconds (will be doubled each retry)
 * @returns Promise with the result of the function
 */
export async function retryWithExponentialBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = API_RETRY_ATTEMPTS,
  delayMs: number = API_RETRY_DELAY_MS
): Promise<T> {
  let lastError: Error;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Don't retry on client errors (4xx) except 429 (rate limit)
      if (error && typeof error === "object" && "response" in error) {
        const status = (error as any).response?.status;
        if (status && status >= 400 && status < 500 && status !== 429) {
          throw error; // Client errors shouldn't be retried
        }
      }

      // If this was the last attempt, throw the error
      if (attempt === maxRetries - 1) {
        throw lastError;
      }

      // Wait before retrying with exponential backoff
      const waitTime = delayMs * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  throw lastError!;
}

/**
 * Checks if an error is a network error
 */
export function isNetworkError(error: any): boolean {
  return (
    error &&
    (error.message === "Network Error" ||
      error.code === "ECONNABORTED" ||
      !error.response)
  );
}
